import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button, Field, Form, TextArea } from '@/components';
import { sanitizeInput } from './helpers';


export type SupportFeedbackType = 'support' | 'feedback';

export interface SupportFeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: SupportFeedbackType;
  onSubmit: (message: string, type: SupportFeedbackType) => Promise<void> | void;
}

export type SupportFeedbackFormFields = {
  message: string;
};

const MAX_CHARACTERS = 400;

const MODAL_CONFIG: Record<
  SupportFeedbackType,
  {
    title: string;
    description: string;
    buttonText: string;
    placeholder: string;
  }
> = {
  support: {
    title: 'Contact Support',
    description:
      'If you have questions about an opportunity, a deal in progress, or how to use the platform, our team is here to help. We typically reply within one business day.',
    buttonText: 'Contact support',
    placeholder: 'Describe your issue or question...',
  },
  feedback: {
    title: 'Share your feedback',
    description:
      'Your feedback helps us improve nTeaser. If something is unclear, missing, or could work better, let us know. We review every message and use it to shape the product.',
    buttonText: 'Send feedback',
    placeholder: 'Share your thoughts...',
  },
};


export function SupportFeedbackModal({ open, onOpenChange, type, onSubmit }: SupportFeedbackModalProps) {
  const { t } = useTranslation(['sidebar']);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [messageLength, setMessageLength] = React.useState(0);

  const config = MODAL_CONFIG[type];
  const remainingChars = MAX_CHARACTERS - messageLength;

  const options = React.useMemo(
    () => ({
      defaultValues: {
        message: '',
      },
    }),
    []
  );

  React.useEffect(() => {
    if (!open) {
      setError(null);
      setIsSubmitting(false);
      setMessageLength(0);
    }
  }, [open]);

  const handleFormSubmit = async (data: SupportFeedbackFormFields) => {
    const trimmedMessage = data.message.trim();

    if (trimmedMessage.length === 0) {
      setError(t('Please enter a message'));
      return;
    }

    if (trimmedMessage.length > MAX_CHARACTERS) {
      setError(t('Message is too long'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const sanitizedMessage = sanitizeInput(trimmedMessage);

      if (sanitizedMessage.trim().length === 0) {
        setError(t('Invalid message format'));
        setIsSubmitting(false);
        return;
      }

      await onSubmit(sanitizedMessage, type);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('An error occurred. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription className="pt-2">{config.description}</DialogDescription>
        </DialogHeader>

        <Form<SupportFeedbackFormFields> id="support-feedback-form" onSubmit={handleFormSubmit} options={options}>
          {({ formState, register, watch }) => {
            const currentMessage = watch('message') || '';

            React.useEffect(() => {
              setMessageLength(currentMessage.length);
            }, [currentMessage]);

            const isValid = currentMessage.trim().length > 0 && currentMessage.length <= MAX_CHARACTERS;

            return (
              <>
                <div className="py-4">
                  <Field
                    id="message"
                    label={t('Message')}
                    error={formState.errors['message']}
                    delay={0}
                    classes={{ field: 'space-y-1' }}
                  >
                    <TextArea
                      id="message"
                      placeholder={config.placeholder}
                      registration={register('message', {
                        required: t('This field is required'),
                        maxLength: {
                          value: MAX_CHARACTERS,
                          message: t('Message cannot exceed {{max}} characters', { max: MAX_CHARACTERS }),
                        },
                        validate: (value) => {
                          const trimmed = value?.trim() || '';
                          if (trimmed.length === 0) {
                            return t('Message cannot be empty');
                          }
                          return true;
                        },
                      })}
                      disabled={isSubmitting}
                      className="resize-none"
                    />
                    <div className="mt-1 flex items-center justify-between text-sm">
                      <span className={remainingChars < 50 ? 'text-amber-600' : 'text-muted-foreground'}>
                        {remainingChars} {t('characters remaining')}
                      </span>
                    </div>
                  </Field>
                </div>

                <DialogFooter>
                  <Button type="button" color="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                    {t('Cancel')}
                  </Button>
                  <Button type="submit" color="primary" disabled={!isValid || isSubmitting}>
                    {isSubmitting ? t('Sending...') : config.buttonText}
                  </Button>
                </DialogFooter>
              </>
            );
          }}
        </Form>
      </DialogContent>
    </Dialog>
  );
}
